import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, FileText } from 'lucide-react';
import CrmCoreListPage from './components/CrmCoreListPage';
import { useCrmClientPartyPicker } from './crmPickerOptions';
import { resolveClientPartySelection } from '../../shared/utils/crmCrossModuleUtils';

const DocumentsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.documentsPage.${key}`, opts);
  const { partyOptions, contacts, clients } = useCrmClientPartyPicker();

  const statusOptions = useMemo(() => [
    { value: 'active', label: p('status_active', { defaultValue: 'نشط' }) },
    { value: 'archived', label: p('status_archived', { defaultValue: 'مؤرشف' }) },
    { value: 'expired', label: p('status_expired', { defaultValue: 'منتهي' }) },
  ], [p]);

  const docTypeOptions = useMemo(() => [
    { value: 'contrat', label: p('type_contract', { defaultValue: 'عقد' }) },
    { value: 'proposal', label: p('type_proposal', { defaultValue: 'عرض' }) },
    { value: 'invoice', label: p('type_invoice', { defaultValue: 'فاتورة' }) },
    { value: 'other', label: p('type_other', { defaultValue: 'أخرى' }) },
  ], [p]);

  const categoryOptions = useMemo(() => [
    { value: 'general', label: p('category_general', { defaultValue: 'عام' }) },
    { value: 'legal', label: p('category_legal', { defaultValue: 'قانوني' }) },
    { value: 'commercial', label: p('category_commercial', { defaultValue: 'تجاري' }) },
  ], [p]);

  const resolvePartyLabel = (partyKey, fallback = '') => {
    if (!partyKey) return fallback;
    return resolveClientPartySelection(partyKey, { contacts, clients }).displayName || fallback;
  };

  return (
    <CrmCoreListPage
      pageKey="documentsPage"
      apiPath="/crm/documents"
      icon={FolderOpen}
      entityRef={{ field: 'doc_ref', prefixKey: 'interaction', fallback: 'CRMDOC' }}
      statusOptions={statusOptions}
      mapRecord={(r) => ({
        ...r,
        contact_id: r.contact_id != null && r.contact_id !== '' ? String(r.contact_id) : '',
        contact_label: resolvePartyLabel(r.contact_id, r.contact_name) || '—',
        doc_type_label: docTypeOptions.find((o) => o.value === r.doc_type)?.label || r.doc_type || '—',
        category_label: categoryOptions.find((o) => o.value === r.category)?.label || r.category || '—',
      })}
      buildPayload={(form) => {
        const party = resolveClientPartySelection(form.contact_id, { contacts, clients });
        const storedPartyKey = form.contact_id ? String(form.contact_id) : null;
        const linkedContactId = party.contactId ? String(party.contactId) : storedPartyKey;
        return {
          title: (form.title || '').trim(),
          doc_type: form.doc_type || 'contrat',
          category: form.category || 'general',
          contact_id: linkedContactId,
          contact_name: party.displayName || form.contact_name || null,
          status: form.status || 'active',
          doc_date: form.doc_date || null,
          notes: form.notes || '',
        };
      }}
      formTabs={[
        { id: 'general', label: p('tab_general', { defaultValue: 'عام' }), icon: FolderOpen },
        { id: 'notes', label: p('tab_notes', { defaultValue: 'ملاحظات' }), icon: FileText, iconClassName: 'text-purple-400' },
      ]}
      fields={[
        { name: 'title', labelKey: 'field_title', required: true, tab: 'general' },
        { name: 'contact_id', labelKey: 'field_contact', type: 'select', options: partyOptions, tab: 'general' },
        { name: 'doc_type', labelKey: 'field_type', type: 'select', options: docTypeOptions, defaultValue: 'contrat', tab: 'general' },
        { name: 'category', labelKey: 'field_category', type: 'select', options: categoryOptions, defaultValue: 'general', tab: 'general' },
        { name: 'status', labelKey: 'field_status', type: 'select', options: statusOptions, defaultValue: 'active', tab: 'general' },
        { name: 'doc_date', labelKey: 'field_date', inputType: 'date', tab: 'general' },
        { name: 'notes', labelKey: 'field_notes', type: 'textarea', fullWidth: true, tab: 'notes' },
      ]}
      columns={[
        { key: 'doc_ref', labelKey: 'col_ref' },
        { key: 'title', labelKey: 'col_title' },
        { key: 'contact_label', labelKey: 'col_contact' },
        { key: 'doc_type_label', labelKey: 'col_type' },
        { key: 'category_label', labelKey: 'col_category' },
        { key: 'status', labelKey: 'col_status', type: 'status' },
        { key: 'doc_date', labelKey: 'col_date', type: 'date' },
      ]}
    />
  );
};

export default DocumentsPage;
